import React, { useState } from "react";
import { of, tap, switchMap, catchError, map } from "rxjs";
import PropTypes from "prop-types";
import Track from "../../../models/Track";
import ZoomToRegion from "../../../components/TooltipActions/ZoomToRegion";
import getApiClient from "../../../apiClient";
import Transcript from "../models/Transcript";
import Exon from "../models/Exon";
import Codon from "../models/Codon";
import TranscriptInfo from "./TranscriptInfo";

const btnCl = "tw-px-2 tw-py-1 tw-rounded tw-border";
const enabledCl = "tw-cursor-pointer tw-text-primary tw-border-primary";
const disabledCl =
  "tw-cursor-not-allowed tw-text-gray-400 tw-border-gray-400 tw-opacity-50";

function TranscriptTooltip({ item, track, requestClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const { transcript } = item;
  const {
    level,
    geneSymbol,
    transcriptName: name,
    totalGeneTranscripts,
  } = transcript || {};
  const { chromosome: chr } = transcript?.region || {};

  const geneDict = track.controller.getGeneDict();
  const allGeneTranscripts = geneDict.get(geneSymbol);
  const allOthers = allGeneTranscripts.filter((x) => x.transcriptName !== name);
  const remainingTranscripts = totalGeneTranscripts - 1;

  function handleSelection() {
    const { transcriptName } = item.transcript;
    track.setSharedState({ selectedTranscript: transcriptName });
    requestClose();
  }

  function selectShowTranscripts() {
    const areaPadding = 100000;
    const { region } = item.transcript;
    const { size, id, genomeId } = region.chromosome;
    const x0 = Math.max(0, region.x0 - areaPadding);
    const x1 = Math.min(size, region.x1 + areaPadding);
    const params = { genomeId, chrId: id, x0, x1 };

    of(null)
      .pipe(
        tap(() => setIsLoading(true)),
        switchMap(() => getApiClient().loadAllTranscripts(params)),
        map((x) => x.filter((y) => y.gene === geneSymbol)),
        tap((res) => track.controller.onDataReceived({ res, chr }, true)),
        catchError(() => of(null)),
        tap(() => setIsLoading(false))
      )
      .subscribe(() => {
        requestClose();
      });
  }

  function deleteTranscripts() {
    track.controller.keepTranscriptForGene(transcript);
    requestClose();
  }

  return transcript ? (
    <div>
      <TranscriptInfo
        item={item}
        actions={
          <div
            className="tw-flex-grow tw-text-right"
            data-tooltip="Zoom to region of this item"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            <ZoomToRegion
              region={transcript.region}
              jumpTo={track.zoomService.jumpTo}
              requestClose={requestClose}
            />
          </div>
        }
      />

      <div className="tw-h-4" />

      <div className="tw-flex tw-items-center">
        <div className="tw-flex-grow" />
        {allOthers.length ? (
          <div
            onClick={deleteTranscripts}
            className={`${btnCl} ${allOthers.length ? enabledCl : disabledCl}`}
          >
            Remove other transcripts for {geneSymbol} ({allOthers.length})
          </div>
        ) : (
          <div
            onClick={selectShowTranscripts}
            className={`${btnCl} ${enabledCl} ${
              remainingTranscripts
                ? ""
                : "tw-disabled tw-cursor-not-allowed tw-opacity-50"
            }`}
          >
            Load all transcripts for {geneSymbol} ({remainingTranscripts})
            <i
              className={`${
                isLoading ? "tw-opacity-100" : "tw-opacity-0"
              } tw-ml-1 tw-px-1 tw-text-primary fa fa-circle-o-notch fa-spin`}
            />
          </div>
        )}
        <div className="tw-w-2" />
        <div
          role="button"
          onClick={handleSelection}
          className={`${btnCl} ${level > 0 ? enabledCl : disabledCl}`}
        >
          Move to 1st row
        </div>
      </div>
    </div>
  ) : null;
}

TranscriptTooltip.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.instanceOf(Transcript),
    PropTypes.instanceOf(Exon),
    PropTypes.instanceOf(Codon),
  ]).isRequired,
  track: PropTypes.instanceOf(Track).isRequired,
  requestClose: PropTypes.func.isRequired,
};

TranscriptTooltip.defaultProps = {};

export default TranscriptTooltip;
